import AppComponent from '../base/app-component.ts';
import type { DataTable, ColumnDef } from './data-table.ts';
import { getCasseMap } from '../lib/casse-info.ts';
import { getSindacatiMap } from '../lib/sindacati-info.ts';

const COLUMNS: ColumnDef[] = [
  { key: 'tipo', label: 'Tipo', type: 'text' },
  { key: 'sigla', label: 'Sigla', type: 'text', sortable: true },
  { key: 'cassa', label: 'Cassa', type: 'text' },
  { key: 'lavoratore.cognome', label: 'Lavoratore', type: 'link', href: '/lavoratori/{{lavoratoreId}}', text: '{{lavoratore.cognome}} {{lavoratore.nome}}' },
  { key: 'lavoratore.codiceFiscale', label: 'CF', type: 'code' },
  { key: 'lavoratore.dataNascita', label: 'Data nascita', type: 'date', emptyText: '-' },
  { key: 'dataDisdetta', label: 'Data disdetta', type: 'date', sortable: true },
];

const ACTIONS = JSON.stringify([
  { name: 'elimina', icon: 'trash', btnClass: 'btn-outline-danger', title: 'Elimina disdetta' },
]);

export class TabellaDisdette extends AppComponent {
  static override componentName = 'wc-tabella-disdette';
  protected override get templateId() { return 'tabella-disdette'; }

  private dataTable: DataTable | null = null;
  private currentSort: string | null = null;
  private currentSortDir: 'asc' | 'desc' | null = null;
  private currentFilter = '';
  private currentPage = 1;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private offcanvas: any = null;
  private messageListenerKey: symbol | null = null;

  protected override buildRenderingData(): Record<string, unknown> {
    return {
      ...super.buildRenderingData(),
      columnsJson: JSON.stringify(COLUMNS),
      actionsJson: ACTIONS,
      casse: Object.values(getCasseMap()),
      sindacati: Object.values(getSindacatiMap()),
    };
  }

  protected override afterRender(): void {
    this.dataTable = this.$('wc-data-table') as DataTable | null;
    if (!this.dataTable) return;

    this.setupTableEvents();
    this.setupFiltriAvanzati();
    this.setupFiltriRapidi();
    this.setupMessageListener();
    this.caricaDisdette();
  }

  private setupTableEvents(): void {
    if (!this.dataTable) return;

    this.listen(this.dataTable, 'dt-page-change', (e: Event) => {
      this.currentPage = (e as CustomEvent).detail.page;
      this.caricaDisdette();
    });

    this.listen(this.dataTable, 'dt-sort-change', (e: Event) => {
      const { sort, dir } = (e as CustomEvent).detail;
      this.currentSort = sort;
      this.currentSortDir = dir;
      this.currentPage = 1;
      this.caricaDisdette();
    });

    this.listen(this.dataTable, 'dt-filter-change', (e: Event) => {
      this.currentFilter = (e as CustomEvent).detail.filter;
      this.currentPage = 1;
      this.caricaDisdette();
    });

    this.listen(this.dataTable, 'dt-row-action', (e: Event) => {
      const { action, row } = (e as CustomEvent).detail;
      if (action === 'elimina') this.handleElimina(row);
      if (action === 'crea-delega') this.handleCreaDelega(row);
    });
  }

  private setupFiltriAvanzati(): void {
    const offcanvasEl = this.$('[data-offcanvas="filtri"]');
    if (offcanvasEl) {
      this.offcanvas = new (globalThis as any).bootstrap.Offcanvas(offcanvasEl);
    }

    this.delegate.on('click', '[data-action="apri-filtri"]', () => {
      this.offcanvas?.show();
    });

    const form = this.$('[data-form="filtri-avanzati"]') as HTMLFormElement | null;
    if (form) {
      this.listen(form, 'submit', (e: Event) => {
        e.preventDefault();
        this.offcanvas?.hide();
        this.syncButtonGroup('tipo');
        this.syncButtonGroup('cassa');
        this.currentPage = 1;
        this.caricaDisdette();
      });
    }

    this.delegate.on('click', '[data-action="reset-filtri"], [data-action="reset-filtri-toolbar"]', () => {
      this.resetFiltri();
      this.offcanvas?.hide();
      this.currentPage = 1;
      this.caricaDisdette();
    });
  }

  private statoDisdettaAttivo = '';

  private setupFiltriRapidi(): void {
    this.delegate.on('click', '[data-filtro-rapido] button', (e: Event) => {
      const btn = (e.target as HTMLElement).closest('button') as HTMLElement;
      const group = btn.closest('[data-filtro-rapido]') as HTMLElement;
      const campo = group.dataset.filtroRapido!;
      const value = btn.dataset.value!;
      const wasActive = btn.classList.contains('active');

      group.querySelectorAll('button').forEach(b => b.classList.remove('active'));
      if (!wasActive) btn.classList.add('active');

      if (campo === 'stato-disdetta') {
        this.statoDisdettaAttivo = wasActive ? '' : value;
      } else {
        const select = this.$(`[data-filtro-val="${campo}"]`) as HTMLSelectElement | null;
        if (select) select.value = wasActive ? '' : value;
      }

      this.currentPage = 1;
      this.caricaDisdette();
    });
  }

  private setupMessageListener(): void {
    const handler = (e: MessageEvent) => {
      if (e.data?.action === 'modal-close' && e.data.refresh) {
        this.caricaDisdette();
      }
    };
    this.messageListenerKey = this.listen(window, 'message', handler as EventListener);
  }

  private syncButtonGroup(campo: string): void {
    const select = this.$(`[data-filtro-val="${campo}"]`) as HTMLSelectElement | null;
    const group = this.$(`[data-filtro-rapido="${campo}"]`);
    if (!select || !group) return;
    const val = select.value;
    group.querySelectorAll('button').forEach(b => {
      const btn = b as HTMLElement;
      btn.classList.toggle('active', btn.dataset.value === val);
    });
  }

  private raccogliFiltri(): Record<string, string> {
    const params: Record<string, string> = {};

    for (const campo of ['tipo', 'sigla', 'cassa', 'origine']) {
      const val = (this.$(`[data-filtro-val="${campo}"]`) as HTMLSelectElement | null)?.value;
      if (val) params[campo] = val;
    }

    // Filtro rapido stato-disdetta → tradotto in tipo + sigla
    if (this.statoDisdettaAttivo) {
      switch (this.statoDisdettaAttivo) {
        case 'INVIATA':
          params.tipo = 'INVIATA';
          break;
        case 'RICEVUTA':
          params.tipo = 'RICEVUTA';
          break;
        case 'LIBERO':
          params.tipo = 'GENERICO';
          params.sigla = 'NESSUNO';
          break;
        case 'ATTESA':
          params.tipo = 'GENERICO';
          params.sigla = '_NULL';
          break;
      }
    }

    const da = (this.$('[data-filtro-val="da"]') as HTMLInputElement | null)?.value;
    const a = (this.$('[data-filtro-val="a"]') as HTMLInputElement | null)?.value;
    if (da) params.da = da;
    if (a) params.a = a;

    return params;
  }

  private resetFiltri(): void {
    const form = this.$('[data-form="filtri-avanzati"]') as HTMLFormElement | null;
    form?.reset();
    this.statoDisdettaAttivo = '';
    const statoGroup = this.$('[data-filtro-rapido="stato-disdetta"]');
    statoGroup?.querySelectorAll('button').forEach(b => b.classList.remove('active'));
    this.syncButtonGroup('cassa');
  }

  private updateFiltriCount(): void {
    const filtri = this.raccogliFiltri();
    const count = Object.keys(filtri).length;
    const badge = this.$('[data-filtri-count]');
    if (badge) {
      badge.textContent = count > 0 ? String(count) : '';
      badge.classList.toggle('d-none', count === 0);
    }
    const resetBtn = this.$('[data-action="reset-filtri-toolbar"]');
    resetBtn?.classList.toggle('d-none', count === 0);
  }

  private async caricaDisdette(): Promise<void> {
    if (!this.dataTable) return;
    this.dataTable.setLoading();

    const filtri = this.raccogliFiltri();
    this.updateFiltriCount();

    const result = await this.api.disdette.listaPaginata({
      ...filtri,
      nascosta: '0',
      page: this.currentPage,
      limit: 30,
      sort: this.currentSort ?? undefined,
      sortDir: this.currentSortDir ?? undefined,
      q: this.currentFilter || undefined,
    });

    if (!result.ok) {
      this.dataTable.setError(result.error);
      return;
    }

    const { disdette, count, totalPages, pageSize, page } = result.data;
    this.dataTable.setData(disdette as unknown as Record<string, unknown>[], {
      page, totalPages, count, pageSize,
    });
  }

  private handleCreaDelega(row: Record<string, unknown>): void {
    const id = row.id as number;
    const cassa = row.cassa as string;
    window.location.href = `/deleghe/nuova?fromDisdetta=${id}&defaultCassa=${cassa}`;
  }

  private async handleElimina(row: Record<string, unknown>): Promise<void> {
    const id = row.id as number;
    const tipo = row.tipo as string;
    const sigla = (row.sigla as string) || 'generico';
    const lav = row.lavoratore as { cognome: string; nome: string } | null;
    const nome = lav ? `${lav.cognome} ${lav.nome}` : 'sconosciuto';

    const ok = await Weext.confirm(
      `Eliminare la disdetta ${tipo} ${sigla} per ${nome}?`,
      { title: 'Conferma eliminazione', confirmText: 'Elimina', confirmClass: 'btn-danger' },
    );
    if (!ok) return;

    const result = await this.api.disdette.elimina(id);
    if (result.ok) {
      Weext.toast('Disdetta eliminata', { type: 'success' });
      this.caricaDisdette();
    } else {
      Weext.toast(result.error, { type: 'danger' });
    }
  }

  protected override onDisconnected(): void {
    this.offcanvas?.dispose();
    if (this.messageListenerKey) {
      this.unlisten(this.messageListenerKey);
      this.messageListenerKey = null;
    }
  }
}
